/* cf-compare-store.js — source de vérité unique du comparateur.

   Trois responsabilités, et elles vont ensemble :
   1. tenir l'état (handles de produits) et le persister ;
   2. lire et écrire le contrat d'URL `?p=<handles>` (D63) ;
   3. **activer les contrôles de comparaison de toute la page** (règle
      d'activation centralisée).

   Personne d'autre n'écrit dans `localStorage` ni ne retire l'attribut `hidden`
   d'un contrôle : ni la carte, ni la grille, ni la fiche produit. Une seule
   implémentation, un seul endroit à corriger.

   Sans ce script, les contrôles restent `hidden` et le reste de la page — cartes,
   fiches, navigation — demeure entièrement lisible et utilisable. C'est
   l'amélioration progressive telle que la règle 9 l'exige : le contenu survit,
   pas nécessairement chaque outil.

   `localStorage` n'est pas une source d'existence produit : un handle supprimé,
   renommé ou dépublié y survit. La purge est **autoritaire côté serveur**, et
   elle a un seul déclencheur : un 404 confirmé sur
   `/products/<handle>?section_id=compare-product`. Ni l'absence d'un handle du
   balisage d'une page, ni une panne réseau ne prouvent quoi que ce soit. */

const CF_COMPARE_KEY = 'cf-compare:v1';

/* D61 — plafond fixe du thème, 4 produits. Ce n'est pas un réglage marchand.
   Le même littéral est répété dans `cf-compare-drawer.js` : la duplication est
   volontaire. Le store plafonne à l'écriture, le tiroir replafonne à l'affichage,
   et aucun des deux ne fait confiance à un `localStorage` que n'importe qui peut
   éditer. Le Liquid ne peut pas plafonner : Shopify n'expose pas `?p=` aux
   templates, donc aucune section ne voit la sélection. */
const CF_COMPARE_MAX = 4;

class CfCompareStore extends HTMLElement {
  connectedCallback() {
    this.items = [];
    this.memoryOnly = false;

    /* Ordre volontaire : on révèle les contrôles AVANT de publier. Un abonné qui
       gère sa propre vacuité — le tiroir, vide, doit rester masqué — a ainsi le
       dernier mot sur son affichage. L'inverse le ferait clignoter. */
    this.readInitialState();
    this.activateControls();
    this.publish();

    this.onToggle = this.onToggle.bind(this);
    document.addEventListener('change', this.onToggle);
  }

  disconnectedCallback() {
    document.removeEventListener('change', this.onToggle);
  }

  /* ---------- Lecture ---------- */

  readInitialState() {
    const fromUrl = this.readUrl();

    /* `?p=` prime sur le stockage : une URL partagée doit montrer ce qu'elle
       annonce, pas ce que le visiteur avait comparé hier. Le stockage se
       synchronise ensuite dessus. */
    if (fromUrl.length > 0) {
      this.items = fromUrl.slice(0, CF_COMPARE_MAX);
      this.write();
    } else {
      this.items = this.read().slice(0, CF_COMPARE_MAX);
    }

    /* Aucune purge ici. Une page ne connaît qu'un sous-ensemble du catalogue :
       l'absence d'un handle du balisage courant ne prouve rien. Seule une
       résolution serveur définitive — un 404 sur
       `/products/<handle>?section_id=compare-product` — autorise à retirer un
       handle, et c'est `cf-compare-table.js` qui la demande. */
  }

  readUrl() {
    try {
      const value = new URLSearchParams(window.location.search).get('p');
      if (!value) return [];
      return this.normalise(value.split(','));
    } catch (error) {
      return [];
    }
  }

  /* `localStorage` peut lever, pas seulement contenir du périmé : navigation
     privée, quota dépassé, stockage désactivé par la politique du navigateur.
     Dans ce cas le comparateur fonctionne en mémoire pour la session et rien
     ne casse à l'écran. */
  read() {
    try {
      const raw = window.localStorage.getItem(CF_COMPARE_KEY);
      if (!raw) return [];
      const parsed = JSON.parse(raw);
      if (!Array.isArray(parsed)) return [];
      return this.normalise(parsed);
    } catch (error) {
      this.memoryOnly = true;
      return [];
    }
  }

  write() {
    if (this.memoryOnly) return;
    try {
      window.localStorage.setItem(CF_COMPARE_KEY, JSON.stringify(this.items));
    } catch (error) {
      this.memoryOnly = true;
    }
  }

  /* Tolère un format ancien ou corrompu sans jeter tout l'état : ce qui n'est
     pas un handle exploitable est ignoré, le reste survit. */
  normalise(list) {
    const out = [];
    list.forEach((entry) => {
      let handle = null;
      if (typeof entry === 'string') handle = entry;
      else if (entry && typeof entry.h === 'string') handle = entry.h;
      if (!handle) return;
      handle = handle.trim();
      if (handle.length === 0 || out.indexOf(handle) !== -1) return;
      out.push(handle);
    });
    return out;
  }

  /* ---------- Écriture ---------- */

  onToggle(event) {
    const input = event.target.closest('[data-cf-compare]');
    if (!input) return;
    const handle = input.value;
    if (!handle) return;

    if (input.checked) this.add(handle, input);
    else this.remove(handle);
  }

  add(handle, input) {
    if (this.items.indexOf(handle) !== -1) return;

    /* Le cinquième ajout est refusé proprement : la case revient à son état
       précédent. La note de plafond est déjà visible — `syncLimit` l'a ouverte
       à la publication qui a atteint le plafond. Rien n'est silencieusement
       écrasé. */
    if (this.items.length >= CF_COMPARE_MAX) {
      if (input) input.checked = false;
      return;
    }

    this.items.push(handle);
    this.write();
    this.publish();
  }

  remove(handle) {
    const index = this.items.indexOf(handle);
    if (index === -1) return;
    this.items.splice(index, 1);
    this.write();
    this.publish();
  }

  /* Synchronisation depuis un consommateur qui a résolu la sélection côté
     serveur. On ne republie que si l'état change réellement : sans ce garde,
     table et store se rappelleraient l'un l'autre sans fin. */
  setSelection(handles) {
    const next = this.normalise(handles || []).slice(0, CF_COMPARE_MAX);
    if (next.length === this.items.length && next.every((h, i) => h === this.items[i])) return;
    this.items = next;
    this.write();
    this.publish();
  }

  /* ---------- Diffusion ---------- */

  publish() {
    this.syncControls();
    this.syncCount();
    this.syncLimit();
    this.dispatchEvent(
      new CustomEvent('cf:compare:change', {
        bubbles: true,
        detail: { items: this.items.slice(), max: CF_COMPARE_MAX },
      })
    );
  }

  /* Activation centralisée : les contrôles sont rendus `hidden` par le serveur
     et c'est le store, et lui seul, qui les révèle une fois initialisé. */
  activateControls() {
    document.querySelectorAll('[data-cf-compare-control]').forEach((control) => {
      control.hidden = false;
    });
    this.setAttribute('data-cf-ready', '');
  }

  syncControls() {
    document.querySelectorAll('[data-cf-compare]').forEach((input) => {
      const selected = this.items.indexOf(input.value) !== -1;
      input.checked = selected;
      input.disabled = !selected && this.items.length >= CF_COMPARE_MAX;
    });
  }

  syncCount() {
    document.querySelectorAll('[data-cf-compare-count]').forEach((node) => {
      node.textContent = String(this.items.length);
      node.hidden = this.items.length === 0;
    });
  }

  /* `syncControls` désactive les cases non cochées une fois le plafond atteint.
     Une case désactivée sans explication est une impasse : cette note dit
     pourquoi, et son `role="status"` la fait annoncer. Elle reste visible tant
     que le plafond est atteint — un message qui s'efface après quelques secondes
     laisserait les cases inertes sans raison affichée.

     Le gabarit vient des locales, le nombre du JavaScript : le thème n'écrit
     aucun texte et ne duplique pas le plafond D61 en Liquid. */
  syncLimit() {
    const node = document.querySelector('[data-cf-compare-limit]');
    if (!node) return;
    const reached = this.items.length >= CF_COMPARE_MAX;
    if (reached && node.hidden) {
      const template = node.dataset.limitTemplate || '';
      node.textContent = template.replace('%%max%%', String(CF_COMPARE_MAX));
    }
    node.hidden = !reached;
  }
}

if (!customElements.get('cf-compare-store')) {
  customElements.define('cf-compare-store', CfCompareStore);
}
